import React, { FunctionComponent } from 'react';
import { Grid, Button, Colors, Tooltip, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { Form, Field, FieldArray, Formik, FormikProps, FormikHelpers } from 'formik';
import { FaPlus } from 'react-icons/fa';
import { IoMdTrash } from 'react-icons/io';
import { MdInfoOutline } from 'react-icons/md';
import styled from 'styled-components';
import { object, array, string, number } from 'yup';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// types
import Rules from 'restrictions/roles/constants/rules';
import { Domain } from 'pages/MerchantPages/Settings/Auth/Oauth/components/modals/EnableSSO';

// components
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import Restricted from 'restrictions/roles/components/Restricted';
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';
import FieldError from 'components/Fields/FieldError';

const { Container, Cell } = Grid;

const CellWithScroll = styled(Cell)`
  max-height: 12rem;
  overflow-y: scroll;
`;

const DomainTooltip = styled(Tooltip)`
  position: absolute;
`;

interface FormValues {
  domains: Array<any>;
}

interface OwnProps {
  domains: Array<Domain>;
  nosubDomain: boolean;
  onSubmit: (data: Array<Domain>) => void;
  handleModalClose: () => void;
}

type Props = OwnProps;

const validationSchema = object().shape({
  domains: array().of(
    object().shape({
      id: number(),
      domain: string().required('*Required'),
    })
  ),
});

const DomainsForm: FunctionComponent<Props> = ({ domains, nosubDomain, onSubmit, handleModalClose }) => {
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_OAUTH_AND_SSO_SETUP);

  const onFormSubmit = async (data: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    await onSubmit(data.domains);
    setSubmitting(false);
  };

  const handleClose = (resetForm: () => void) => () => {
    resetForm();
    handleModalClose();
  };

  return (
    <Formik onSubmit={onFormSubmit} initialValues={{ domains }} validationSchema={validationSchema}>
      {({ values, resetForm, isSubmitting, dirty, isValid }: FormikProps<FormValues>) => {
        const disableDeleteButton = values && values.domains.length === 1;
        const disableSaveButton = !nosubDomain && (isSubmitting || !isValid || !dirty);
        return (
          <AnalyticsComponent>
            {({ pages, tracker, merchantId, ip }) => (
              <Form>
                {!nosubDomain && (
                  <FieldArray name="domains">
                    {(arrayHelpers) => (
                      <Container columns={1}>
                        {values &&
                          values.domains.map((d: any, i: number) => {
                            return (
                              <CellWithScroll key={i}>
                                <StyledContainer columns="1fr auto" margin="0 0 0.2rem 0">
                                  <Cell middle>
                                    <Field
                                      tag={`text_input_domain_${i}`}
                                      name={`domains[${i}].domain`}
                                      type="text"
                                      maxLength={255}
                                      component={Input}
                                      placeholder="URL (e.g. https://yourdomain.com)"
                                      disabled={isReadOnly}
                                    />
                                  </Cell>
                                  <Cell middle>
                                    <Restricted isRestricted={isReadOnly || disableDeleteButton}>
                                      <StyledReactIcon
                                        as={IoMdTrash}
                                        color={Colors.red}
                                        onClick={() => {
                                          tracker.track({
                                            event: 'click',
                                            type: 'icon',
                                            tag: `icon_delete_${i}`,
                                            pages,
                                            merchantId,
                                            ip,
                                          });
                                          arrayHelpers.remove(i);
                                        }}
                                      />
                                    </Restricted>
                                    <Restricted isRestricted={!disableDeleteButton}>
                                      <DomainTooltip content="At least one domain must be present">
                                        <MdInfoOutline />
                                      </DomainTooltip>
                                    </Restricted>
                                  </Cell>
                                  <FieldError name={`domains[${i}].domain`} />
                                </StyledContainer>
                              </CellWithScroll>
                            );
                          })}
                        <Cell>
                          <Restricted isRestricted={isReadOnly}>
                            <Button
                              tag="button_add_domain"
                              type="button"
                              onClick={() => arrayHelpers.push({ id: 0, domain: '' })}
                              buttonModifiers={['buttonLink']}
                            >
                              <FaPlus />
                              Add a domain
                            </Button>
                          </Restricted>
                        </Cell>
                      </Container>
                    )}
                  </FieldArray>
                )}
                <StyledContainer columns={3} alignContent="center" justifyContent="center">
                  <Restricted isRestricted={isReadOnly}>
                    <Cell middle left={2}>
                      <Button
                        tag="button_yes"
                        fullWidth
                        type="submit"
                        buttonModifiers={['buttonPrimary']}
                        disabled={disableSaveButton}
                      >
                        {nosubDomain ? 'Confirm' : 'Save'}
                      </Button>
                    </Cell>
                    <Cell middle left={3}>
                      <Button tag="button_cancel" type="button" fullWidth onClick={handleClose(resetForm)}>
                        Cancel
                      </Button>
                    </Cell>
                  </Restricted>
                </StyledContainer>
              </Form>
            )}
          </AnalyticsComponent>
        );
      }}
    </Formik>
  );
};

export default DomainsForm;
